import React, { useState, useEffect } from 'react';
import { Users, FileText, CheckCircle, Clock, TrendingUp, Settings, Shield } from 'lucide-react';
import axios from 'axios';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalPrenups: 0,
    approvedPrenups: 0,
    pendingPrenups: 0,
    rejectedPrenups: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load statistics from admin endpoints
      const [usersResponse, prenupStatsResponse] = await Promise.all([
        axios.get('/admin/users/stats'),
        axios.get('/admin/prenups/stats')
      ]);

      setStats({
        totalUsers: usersResponse.data.data.total || 0,
        totalPrenups: prenupStatsResponse.data.data.total || 0,
        pendingPrenups: prenupStatsResponse.data.data.pending || 0,
        approvedPrenups: prenupStatsResponse.data.data.approved || 0,
        rejectedPrenups: prenupStatsResponse.data.data.rejected || 0
      });

      setRecentActivities(prenupStatsResponse.data.data.recent || []);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className={`w-12 h-12 ${bgColor} rounded-md flex items-center justify-center`}>
              <Icon className={`w-6 h-6 ${color}`} />
            </div>
          </div>
          <div className="mr-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">
                {title}
              </dt>
              <dd className="text-3xl font-bold text-gray-900">
                {loading ? '...' : value.toLocaleString('ar-EG')}
              </dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );

  const ActivityItem = ({ activity, index }) => (
    <div key={index} className="flex items-start space-x-3 space-x-reverse py-3">
      <div className="flex-shrink-0">
        <div className={`w-2 h-2 rounded-full ${
          activity.type === 'approved' ? 'bg-green-400' : 
          activity.type === 'rejected' ? 'bg-red-400' : 
          'bg-yellow-400'
        }`}></div>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900">
          {activity.message || `استمارة جديدة من ${activity.userName || 'مستخدم'}`}
        </p>
        <p className="text-sm text-gray-500">
          {activity.createdAt ? new Date(activity.createdAt).toLocaleDateString('ar-EG') : 'اليوم'}
        </p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">لوحة التحكم الرئيسية</h1>
        <p className="mt-1 text-sm text-gray-600">
          نظرة عامة على النظام والإحصائيات
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="إجمالي المستخدمين"
          value={stats.totalUsers}
          icon={Users}
          color="text-blue-600"
          bgColor="bg-blue-100"
        />
        <StatCard
          title="إجمالي الاستمارات"
          value={stats.totalPrenups}
          icon={FileText}
          color="text-purple-600"
          bgColor="bg-purple-100"
        />
        <StatCard
          title="الاستمارات المعتمدة"
          value={stats.approvedPrenups}
          icon={CheckCircle}
          color="text-green-600"
          bgColor="bg-green-100"
        />
        <StatCard
          title="في انتظار المراجعة"
          value={stats.pendingPrenups}
          icon={Clock}
          color="text-yellow-600"
          bgColor="bg-yellow-100"
        />
      </div>

      {/* Charts and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart Container */}
        <div className="lg:col-span-2 bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              إحصائيات الاستمارات
            </h3>
            
            <div className="space-y-4">
              {/* Progress bars for visual representation */}
              <div>
                <div className="flex justify-between text-sm text-gray-700 mb-1">
                  <span>معتمدة</span>
                  <span>{stats.approvedPrenups}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full" 
                    style={{ 
                      width: stats.totalPrenups > 0 
                        ? `${(stats.approvedPrenups / stats.totalPrenups) * 100}%` 
                        : '0%' 
                    }}
                  ></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between text-sm text-gray-700 mb-1">
                  <span>في انتظار المراجعة</span>
                  <span>{stats.pendingPrenups}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-yellow-600 h-2 rounded-full" 
                    style={{ 
                      width: stats.totalPrenups > 0 
                        ? `${(stats.pendingPrenups / stats.totalPrenups) * 100}%` 
                        : '0%' 
                    }}
                  ></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between text-sm text-gray-700 mb-1">
                  <span>مرفوضة</span>
                  <span>{stats.rejectedPrenups}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-red-600 h-2 rounded-full" 
                    style={{ 
                      width: stats.totalPrenups > 0 
                        ? `${(stats.rejectedPrenups / stats.totalPrenups) * 100}%` 
                        : '0%' 
                    }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Summary stats */}
            <div className="mt-6 grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {stats.totalPrenups > 0 
                    ? Math.round((stats.approvedPrenups / stats.totalPrenups) * 100) 
                    : 0}%
                </div>
                <div className="text-sm text-gray-500">معدل الاعتماد</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-600">
                  {stats.totalPrenups > 0 
                    ? Math.round((stats.pendingPrenups / stats.totalPrenups) * 100) 
                    : 0}%
                </div>
                <div className="text-sm text-gray-500">قيد المراجعة</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600">
                  {stats.totalPrenups > 0 
                    ? Math.round((stats.rejectedPrenups / stats.totalPrenups) * 100) 
                    : 0}%
                </div>
                <div className="text-sm text-gray-500">معدل الرفض</div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              النشاط الأخير
            </h3>
            
            <div className="space-y-3">
              {loading ? (
                <div className="animate-pulse space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex space-x-3 space-x-reverse">
                      <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                      <div className="flex-1 space-y-1">
                        <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                        <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : recentActivities.length > 0 ? (
                recentActivities.slice(0, 5).map((activity, index) => (
                  <ActivityItem key={index} activity={activity} index={index} />
                ))
              ) : (
                <div className="text-center py-4">
                  <TrendingUp className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">لا توجد أنشطة حديثة</p>
                </div>
              )}
            </div>

            {recentActivities.length > 5 && (
              <div className="mt-4 text-center">
                <button className="text-blue-600 hover:text-blue-500 text-sm font-medium">
                  عرض المزيد
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            إجراءات سريعة
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <button className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
              <Users className="w-4 h-4 ml-2" />
              إضافة مستخدم
            </button>
            <button className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
              <Shield className="w-4 h-4 ml-2" />
              إضافة مشرف
            </button>
            <button className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
              <FileText className="w-4 h-4 ml-2" />
              مراجعة الاستمارات
            </button>
            <button className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
              <Settings className="w-4 h-4 ml-2" />
              إعدادات النظام
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;